/**
 * HTTP client + normalization for the deterministic migrations ENGINE.
 *
 * Every read below is normalized rather than cast. The engine's whole product is a
 * verdict, and a verdict read off an unvalidated `as EnginePlanReport` would let a
 * missing field render as a blank, confident-looking success.
 *
 * WHAT CHANGED NOW THAT THE BACKEND EXISTS. This module was written against a specified
 * contract while the API did not, so it hedged: it read two spellings of `residue`, it
 * derived counts the server was already returning, and it invented an artifact filename
 * from a hardcoded extension map. Those hedges are gone. The wire is authoritative
 * (`ncoder-playground` `app/migrations_api.py`), and a normalizer that accepts a shape
 * the server never sends is a branch no test can fail and no reader can trust.
 *
 * WHAT DID NOT CHANGE: absence is still never smoothed into zero. `undeclared_count`
 * and `counts.undeclared_total` fall back to `UNDECLARED_UNKNOWN`, not to `0`, so a
 * malformed response reports "we do not know" instead of "none".
 */
import { apiGet, apiPost, ApiError } from "@/services/studio/client";
import {
  arrayOfStrings,
  asRecord,
  numberOrNull,
  numberValue,
  recordValue,
  stringOrNull,
  stringValue,
} from "@/services/studio/primitives";
import type {
  EngineAdvisory,
  EngineApplyAttempt,
  EngineApplyResult,
  EngineApplySummary,
  EngineApplyVerification,
  EngineApproveRequest,
  EngineApproveResponse,
  EngineArtifactBody,
  EngineArtifactListResponse,
  EngineArtifactRecord,
  EngineBlastEntry,
  EngineBlastRadius,
  EngineCancelResponse,
  EngineClearApplyRequest,
  EngineClearApplyResponse,
  EngineCoverageBucket,
  EngineDecommissionResult,
  EngineDecommissionSummary,
  EngineEffect,
  EngineEffectsPage,
  EngineMigrationPhase,
  EnginePlanCounts,
  EnginePlanListResponse,
  EnginePlanReport,
  EnginePlanScope,
  EnginePlanSummary,
  EngineRefusal,
  EngineResidueItem,
  EngineTaskReport,
  EngineValidation,
  EngineWillCreate,
  EngineWillDeactivate,
} from "./types";
import {
  ENGINE_PLAN_SCOPE_COMPONENT,
  ENGINE_PLAN_SCOPE_ORG,
  UNDECLARED_UNKNOWN,
} from "./types";

export * from "./types";

const BASE = "/api/migrations/plans";

/** `true` on a JSON boolean `true` and on nothing else. Absent reads as `false`. */
function booleanValue(value: unknown): boolean {
  return value === true;
}

/**
 * A task key, safe in one path segment.
 *
 * The server percent-encodes the key for us and returns it as `key_encoded`; that is
 * what to send back. Keys are `t1`, `t2`… today, but the natural key for a Profile task
 * is its own name and `Profile/Admin` exists in real orgs — a raw slash there is a
 * different URL. `encodeURIComponent` is the fallback for a task read from somewhere
 * that has no encoded form.
 */
export function engineTaskPath(task: { key: string; key_encoded?: string }): string {
  return task.key_encoded || encodeURIComponent(task.key);
}

/**
 * The engine API does not exist in every environment. A 404/405/501 from the plans
 * index is not an error to surface — it is "this deployment has no engine", and the
 * whole surface hides itself. Anything else is a real failure and is rethrown.
 */
export function isEngineAbsent(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return error.status === 404 || error.status === 405 || error.status === 501;
}

/** A 409 from approve: nothing on this plan is awaiting a decision. */
export function isApprovalConflict(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * THIS DEPLOYMENT DOES NOT MOUNT THE DECOMMISSION ROUTES.
 *
 * `PLAYGROUND_MIGRATION_DECOMMISSION` is a THIRD flag, nested inside the apply flag and
 * default OFF, and with it off the two routes do not exist — they are not in the route
 * table and not in the OpenAPI document. "It 403s" is deliberately not an acceptable off
 * state for a route that can switch off a customer's automation, so the honest client
 * reading of a 404/405/501 here is the same one `isEngineAbsent` makes: the capability is
 * not here. Anything else is a real failure and is rethrown.
 *
 * The report's `decommission.enabled` says the same thing one round trip earlier and is
 * the primary signal; this is the backstop for a flag that flipped between the read and
 * the write.
 */
export function isDecommissionAbsent(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  return error.status === 404 || error.status === 405 || error.status === 501;
}

export function engineErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    // FastAPI's `detail` is a string on some refusals and an object on others (the 409
    // carries `{error, message, awaiting}`). `client.parseError` only lifts the string
    // form, so the object form arrives as a bare status line unless it is read here.
    const detail = asRecord(asRecord(error.body)?.detail);
    const message = detail ? stringOrNull(detail.message) : null;
    if (message) return message;
    return error.message || `Request failed (${error.status})`;
  }
  return error instanceof Error ? error.message : "Request failed";
}

// ── reads ───────────────────────────────────────────────────────────────────────

export async function listEnginePlans(
  opts: { limit?: number; status?: string; offset?: number } = {}
) {
  const params = new URLSearchParams();
  if (opts.limit != null) params.set("limit", String(opts.limit));
  if (opts.offset != null) params.set("offset", String(opts.offset));
  if (opts.status) params.set("status", opts.status);
  const query = params.toString();
  return normalizePlanList(await apiGet<unknown>(query ? `${BASE}?${query}` : BASE));
}

export async function getEnginePlanReport(planId: string) {
  return normalizePlanReport(await apiGet<unknown>(`${BASE}/${encodeURIComponent(planId)}/report`));
}

export async function listEngineEffects(
  planId: string,
  taskPath: string,
  opts: { bucket: EngineCoverageBucket; limit?: number; offset?: number }
) {
  const params = new URLSearchParams({ bucket: opts.bucket });
  if (opts.limit != null) params.set("limit", String(opts.limit));
  if (opts.offset != null) params.set("offset", String(opts.offset));
  return normalizeEffectsPage(
    await apiGet<unknown>(
      `${BASE}/${encodeURIComponent(planId)}/tasks/${taskPath}/effects?${params}`
    ),
    opts.bucket
  );
}

export async function listEngineArtifacts(planId: string, taskPath: string) {
  return normalizeArtifactList(
    await apiGet<unknown>(`${BASE}/${encodeURIComponent(planId)}/tasks/${taskPath}/artifacts`)
  );
}

/**
 * Path to one artifact's body. `?version=N` reaches a SUPERSEDED revision: artifacts are
 * append-only, so every byte that was ever compiled stays retrievable, and the version
 * is always sent rather than left to default — a screen that shows v2 and downloads
 * "whatever is latest" is a screen that can hand over bytes it never displayed.
 */
export function engineArtifactPath(
  planId: string,
  taskPath: string,
  componentKeyEncoded: string,
  version?: number
): string {
  const suffix = version != null ? `?version=${encodeURIComponent(String(version))}` : "";
  return `${BASE}/${encodeURIComponent(planId)}/tasks/${taskPath}/artifacts/${componentKeyEncoded}${suffix}`;
}

/**
 * One artifact's bytes.
 *
 * The route returns JSON — metadata plus a `content` string — not a file body, so this
 * cannot be a blob download and is not pretended to be one. Saving is done from the
 * string, with the `filename` and `content_type` the SERVER sent.
 */
export async function getEngineArtifact(
  planId: string,
  taskPath: string,
  componentKeyEncoded: string,
  version?: number
): Promise<EngineArtifactBody> {
  const payload = await apiGet<unknown>(
    engineArtifactPath(planId, taskPath, componentKeyEncoded, version)
  );
  const raw = asRecord(payload) ?? {};
  return { ...normalizeArtifactRecord(raw), content: stringValue(raw.content, "") };
}

// ── writes ──────────────────────────────────────────────────────────────────────

export async function createEnginePlan(body: {
  family: string;
  full_name: string;
  title?: string;
}): Promise<{ plan_id: string; status: string }> {
  const raw =
    asRecord(
      await apiPost<unknown>(BASE, {
        family: body.family,
        source: { full_name: body.full_name },
        ...(body.title ? { title: body.title } : {}),
      })
    ) ?? {};
  return { plan_id: stringValue(raw.plan_id, ""), status: stringValue(raw.status, "unknown") };
}

/**
 * Drain the plan. Safe to send twice: `drain` claims what is claimable and holds no
 * state, so a retry or a double-click resumes the same plan rather than starting a
 * second one.
 */
export async function runEnginePlan(planId: string) {
  const raw = asRecord(await apiPost<unknown>(`${BASE}/${encodeURIComponent(planId)}/run`, {})) ?? {};
  return {
    plan_id: stringValue(raw.plan_id, planId),
    status: stringValue(raw.status, "unknown"),
    executed: numberValue(raw.executed, 0),
    outcomes: arrayOfStrings(raw.outcomes),
  };
}

/**
 * Resolve GATE_APPLY (or GATE_ROLLBACK). Approval RESUMES a paused plan and RECORDS A
 * LEDGER ROW NAMING THE EXACT `compile_key` of every task it moves.
 *
 * It does not deploy. That separation is deliberate, not a limitation: a human approving
 * an artifact and a human choosing to write it to an org are different decisions, and an
 * approval that deployed on the spot could not carry the confirmation token for the
 * artifact it approved — the token is displayed beside the decision.
 */
export async function approveEnginePlan(planId: string, body: EngineApproveRequest) {
  const payload = await apiPost<unknown>(`${BASE}/${encodeURIComponent(planId)}/approve`, {
    task_keys: body.task_keys,
    decision: body.decision,
    gate: body.gate ?? "GATE_APPLY",
    ...(body.reason ? { reason: body.reason } : {}),
  });
  const raw = asRecord(payload) ?? {};
  const response: EngineApproveResponse = {
    plan_id: stringValue(raw.plan_id, planId),
    gate: stringValue(raw.gate, body.gate ?? "GATE_APPLY"),
    decision: stringValue(raw.decision, body.decision),
    tasks: arrayOfStrings(raw.tasks),
    status: stringValue(raw.status, "unknown"),
  };
  return response;
}

export async function cancelEnginePlan(planId: string): Promise<EngineCancelResponse> {
  const raw = asRecord(await apiPost<unknown>(`${BASE}/${encodeURIComponent(planId)}/cancel`, {})) ?? {};
  return {
    plan_id: stringValue(raw.plan_id, planId),
    status: stringValue(raw.status, "cancelled"),
    cancel_requested: booleanValue(raw.cancel_requested),
  };
}

/**
 * CREATE the approved component in the org. The only write this surface can perform.
 *
 * ALWAYS 200, INCLUDING ON A REFUSAL, and the response is read accordingly: `sent` is a
 * field, never an inference from a status code. The one thing a caller must not have to
 * work out for itself is whether the org changed, so `sent` and `outcome` are read from
 * the body and a refusal comes back with `sent: false` and a `code` naming the factor.
 *
 * Every field of the request is a gate factor with no safe default, so every field is
 * passed explicitly: `op` (no default — a caller who meant to validate cannot reach a
 * write by omitting a field), `check_only` as the literal boolean `false` (the server
 * refuses anything else, including `true`, rather than downgrading the request), and the
 * verbatim token. Safe to send twice: the second call finds the recorded attempt and
 * either returns its settled result or polls the deploy the first one submitted.
 */
export async function applyEngineTask(
  planId: string,
  taskPath: string,
  confirmation: string
): Promise<EngineApplyResult> {
  const raw =
    asRecord(
      await apiPost<unknown>(`${BASE}/${encodeURIComponent(planId)}/tasks/${taskPath}/apply`, {
        op: "apply",
        check_only: false,
        confirmation,
      })
    ) ?? {};
  return {
    plan_id: stringValue(raw.plan_id, planId),
    task_key: stringValue(raw.task_key, ""),
    outcome: stringValue(raw.outcome, "").toLowerCase(),
    // Fails closed in the only direction that is safe to be wrong in: a response we
    // cannot read reports "nothing was sent", which is the state that makes a reader
    // go and look rather than the state that makes them stop.
    sent: booleanValue(raw.sent),
    code: stringOrNull(raw.code) ?? undefined,
    message: stringOrNull(raw.message) ?? undefined,
    expected: stringOrNull(raw.expected) ?? undefined,
    component_key: stringOrNull(raw.component_key) ?? undefined,
    metadata_type: stringOrNull(raw.metadata_type) ?? undefined,
    compile_key: stringOrNull(raw.compile_key) ?? undefined,
    deploy_id: stringOrNull(raw.deploy_id) ?? undefined,
  };
}

/**
 * DEACTIVATE THE SOURCE the migration replaced. The most dangerous act in this engine.
 *
 * An apply gone wrong leaves an unused Draft Flow — additive, probed absent first,
 * deletable by an undo that can prove it made it. A decommission gone wrong STOPS A
 * BUSINESS PROCESS: nothing is created that can be deleted, something the customer relies
 * on quietly stops happening, and the records that pass through while it is off are never
 * replayed.
 *
 * THE TOKEN NAMES THE SOURCE, not the artifact we compiled — `DEACTIVATE WorkflowRule
 * Account.Set_Rating IN <org> <compile_key>`. It is the one place in this engine where
 * those two are different things, and the server renders it beside the decision so the
 * string a screen shows and the string the gate recomputes cannot drift.
 *
 * Every field is a gate factor with no safe default, passed explicitly for the same reason
 * `applyEngineTask` passes them: `op` (a caller who meant to validate cannot reach a write
 * by omitting a field) and `check_only` as the literal `false` (the server refuses
 * anything else, `true` included, rather than downgrading the request). Always 200,
 * including on a refusal — whether a customer's automation changed must never be inferred
 * from a status code. Safe to send twice: the second call finds the recorded attempt and
 * polls it rather than submitting a second deploy.
 */
export async function decommissionEngineTask(
  planId: string,
  taskPath: string,
  confirmation: string
): Promise<EngineDecommissionResult> {
  return normalizeDecommissionResult(
    await apiPost<unknown>(
      `${BASE}/${encodeURIComponent(planId)}/tasks/${taskPath}/decommission`,
      { op: "decommission", check_only: false, confirmation }
    ),
    planId
  );
}

/**
 * Turn the source back ON. The reverse of a decommission and the way out of a bad one.
 *
 * THE FORWARD PATH FAILS CLOSED AND THIS ONE FAILS OPEN, by design: refusing to turn a
 * customer's automation back on is the one outcome worse than turning on something that
 * was already on, which is a no-op. Its server-side context cannot deactivate at all, so
 * the route that exists to restore automation has no capability to stop it.
 *
 * The token is `REACTIVATE …` and the report does NOT render it — only the deactivate one
 * is issued. It is derived (see `derivedRecommissionToken`) and labelled as derived; if
 * the derivation is ever wrong the server refuses with `CONFIRMATION_MISMATCH` and returns
 * the token it wanted, which is then what the screen shows.
 */
export async function recommissionEngineTask(
  planId: string,
  taskPath: string,
  confirmation: string
): Promise<EngineDecommissionResult> {
  return normalizeDecommissionResult(
    await apiPost<unknown>(
      `${BASE}/${encodeURIComponent(planId)}/tasks/${taskPath}/recommission`,
      { op: "recommission", check_only: false, confirmation }
    ),
    planId
  );
}

/**
 * Close a parked apply attempt by hand. THE OPERATOR ESCAPE HATCH, not a button.
 *
 * `apply.py` parks a task at `applying` when it cannot establish whether a deploy
 * reached the org, and that park has no timeout by design. This is its only door. It
 * reaches no org, it sends nothing, and it does NOT assert the org is untouched — it
 * records that a named person went and looked. Refusals come back as a 200 with
 * `sent: false` and a code, because whether the org changed must never be inferred from
 * a status code.
 */
export async function clearEngineApply(
  planId: string,
  taskPath: string,
  body: EngineClearApplyRequest
): Promise<EngineClearApplyResponse> {
  const raw =
    asRecord(
      await apiPost<unknown>(
        `${BASE}/${encodeURIComponent(planId)}/tasks/${taskPath}/clear-apply`,
        { confirmation: body.confirmation, reason: body.reason }
      )
    ) ?? {};
  return {
    plan_id: stringValue(raw.plan_id, planId),
    task_key: stringValue(raw.task_key, ""),
    outcome: stringValue(raw.outcome, ""),
    // Never defaulted to `true`. `sent` is the one fact a caller must not have to guess.
    sent: booleanValue(raw.sent),
    code: stringOrNull(raw.code) ?? undefined,
    message: stringOrNull(raw.message) ?? undefined,
    component_key: stringOrNull(raw.component_key) ?? undefined,
    compile_key: stringOrNull(raw.compile_key) ?? undefined,
    cleared_by: stringOrNull(raw.cleared_by) ?? undefined,
    task_status: stringOrNull(raw.task_status) ?? undefined,
    reason: stringOrNull(raw.reason) ?? undefined,
    cleared_at: stringOrNull(raw.cleared_at) ?? undefined,
    expected: stringOrNull(raw.expected) ?? undefined,
  };
}

// ── normalization ───────────────────────────────────────────────────────────────

export function normalizePlanList(payload: unknown): EnginePlanListResponse {
  const raw = asRecord(payload);
  const source = Array.isArray(raw?.plans) ? raw.plans : Array.isArray(payload) ? payload : [];
  return {
    plans: source.map(normalizePlanSummary),
    limit: numberValue(raw?.limit, source.length),
    offset: numberValue(raw?.offset, 0),
  };
}

/**
 * One row of `GET /plans`. THE INDEX CARRIES NO COVERAGE — `_wire_plan` returns ten
 * fields and none of them is a count. Nothing is invented here to fill that gap: a
 * plan-list row that printed "0 undeclared" would be asserting a verdict the endpoint
 * never gave it, which is the same failure as printing a clean zero for a missing field.
 */
export function normalizePlanSummary(input: unknown): EnginePlanSummary {
  const raw = asRecord(input) ?? {};
  return {
    plan_id: stringValue(raw.plan_id, ""),
    org_id: stringValue(raw.org_id, ""),
    family: stringValue(raw.family, ""),
    title: stringValue(raw.title, ""),
    scope: normalizePlanScope(raw.scope),
    status: stringValue(raw.status, "unknown"),
    engine_version: stringValue(raw.engine_version, ""),
    transform_version: stringValue(raw.transform_version, ""),
    cancel_requested: booleanValue(raw.cancel_requested),
    created_at: stringValue(raw.created_at, ""),
    updated_at: stringValue(raw.updated_at, ""),
  };
}

/**
 * `component` or `org`, and `""` for anything else INCLUDING an absent field.
 *
 * NOT defaulted to `component`. `_wire_plan` emits this precisely so a UI no longer has to
 * string-match a title to tell a portfolio pass from a single-component plan, and a client
 * that filled the gap with the cheaper of the two values would resurrect exactly the
 * mislabelling the field was added to end — silently, and on the row that costs three
 * orders of magnitude more. A scope the server did not state is a scope this client does
 * not state either.
 */
export function normalizePlanScope(value: unknown): EnginePlanScope {
  if (value === ENGINE_PLAN_SCOPE_ORG) return ENGINE_PLAN_SCOPE_ORG;
  if (value === ENGINE_PLAN_SCOPE_COMPONENT) return ENGINE_PLAN_SCOPE_COMPONENT;
  return "";
}

/**
 * The plan-level counts. `undeclared_total` falls back to UNKNOWN and never to 0 — see
 * the note on the constant.
 */
export function normalizeCounts(input: unknown): EnginePlanCounts {
  const raw = asRecord(input) ?? {};
  return {
    tasks: numberValue(raw.tasks, 0),
    validated: numberValue(raw.validated, 0),
    unvalidated: numberValue(raw.unvalidated, 0),
    validation_failed: numberValue(raw.validation_failed, 0),
    abstained: numberValue(raw.abstained, 0),
    refused: numberValue(raw.refused, 0),
    source_partial: numberValue(raw.source_partial, 0),
    undeclared_total: numberOrNull(raw.undeclared_total) ?? UNDECLARED_UNKNOWN,
    carried_total: numberValue(raw.carried_total, 0),
    total_effects: numberValue(raw.total_effects, 0),
    applied: numberValue(raw.applied, 0),
  };
}

/** The report is FLAT — `plan_report` returns the plan's own fields at the top level. */
export function normalizePlanReport(payload: unknown): EnginePlanReport {
  const raw = asRecord(payload) ?? {};
  const tasks = (Array.isArray(raw.tasks) ? raw.tasks : []).map(normalizeTaskReport);
  const counts = normalizeCounts(raw.counts);
  return {
    plan_id: stringValue(raw.plan_id, ""),
    org_id: stringOrNull(raw.org_id) ?? undefined,
    title: stringOrNull(raw.title) ?? undefined,
    status: stringValue(raw.status, "unknown"),
    family: stringValue(raw.family, ""),
    created_at: stringOrNull(raw.created_at) ?? undefined,
    updated_at: stringOrNull(raw.updated_at) ?? undefined,
    engine_version: stringOrNull(raw.engine_version) ?? undefined,
    transform_version: stringOrNull(raw.transform_version) ?? undefined,
    menus_sha: stringOrNull(raw.menus_sha) ?? undefined,
    tasks,
    counts,
    wrote_to_org: booleanValue(raw.wrote_to_org),
    applied: numberValue(raw.applied, 0),
    // The server says this out loud; default `true` because the report NEVER carries the
    // carried list, and a UI that quietly stopped saying so would imply it had one.
    carried_omitted: raw.carried_omitted === undefined ? true : booleanValue(raw.carried_omitted),
    effects_path: stringOrNull(raw.effects_path) ?? undefined,
    // The flat sibling. Falls back to `counts`, then to UNKNOWN — never to a sum over
    // `tasks`, which is a sum over whatever page of tasks this payload happened to hold.
    undeclared_total: numberOrNull(raw.undeclared_total) ?? counts.undeclared_total,
  };
}

export function normalizeTaskReport(input: unknown): EngineTaskReport {
  const raw = asRecord(input) ?? {};
  const key = stringValue(raw.key, "");
  const undeclared = normalizeEffects(raw.undeclared);
  return {
    id: stringValue(raw.id, ""),
    key,
    key_encoded: stringValue(raw.key_encoded, encodeURIComponent(key)),
    status: stringValue(raw.status, "unknown"),
    source: recordValue(raw.source),

    source_route: stringValue(raw.source_route, ""),
    // Present even when true on the wire; `false` is the meaningful value, so an absent
    // field reads as "not complete" rather than as a reassuring default.
    source_complete: booleanValue(raw.source_complete),
    source_counts: normalizeSourceCounts(raw.source_counts),
    source_unreadable: arrayOfStrings(raw.source_unreadable),
    source_unverified: arrayOfStrings(raw.source_unverified),

    menus_resolved: arrayOfStrings(raw.menus_resolved),
    menus_unavailable: arrayOfStrings(raw.menus_unavailable),
    scope_objects: arrayOfStrings(raw.scope_objects),
    menus_sha: stringValue(raw.menus_sha, ""),
    menus_scope_sha: stringValue(raw.menus_scope_sha, ""),

    carried_count: numberValue(raw.carried_count, 0),
    total_effects: numberValue(raw.total_effects, 0),
    carried_fraction: numberValue(raw.carried_fraction, 0),
    // ONE SPELLING, THE SERVER'S. `weakened` is what `EFFECT_BUCKETS` pages and what every
    // family's `coverage_report` builds; no alternative is read "in case", because a
    // fallback that never fires is a branch no test can fail.
    weakened: normalizeEffects(raw.weakened),
    dropped_with_reason: normalizeEffects(raw.dropped_with_reason),
    unrepresentable: normalizeEffects(raw.unrepresentable),
    rejected: normalizeEffects(raw.rejected),
    undeclared,

    // THE COUNT, not the length of the list beside it. `/report` paginates; the counts
    // do not. Absent reads as UNKNOWN so a malformed payload cannot report "0 lost".
    undeclared_count: numberOrNull(raw.undeclared_count) ?? UNDECLARED_UNKNOWN,
    rejected_count: numberOrNull(raw.rejected_count) ?? UNDECLARED_UNKNOWN,
    dropped_count: numberOrNull(raw.dropped_count) ?? UNDECLARED_UNKNOWN,
    unrepresentable_count: numberOrNull(raw.unrepresentable_count) ?? UNDECLARED_UNKNOWN,
    // The report does not carry this today even though the bucket pages, so it is normally
    // UNKNOWN — which renders as `?`. A `0` here would be this client asserting a clean
    // result the server never gave it, on the one bucket nothing else on the screen counts.
    weakened_count: numberOrNull(raw.weakened_count) ?? UNDECLARED_UNKNOWN,
    residue_count: numberOrNull(raw.residue_count) ?? UNDECLARED_UNKNOWN,

    residue: normalizeResidue(raw.residue),

    advisories: normalizeAdvisories(raw.advisories),
    trigger: recordValue(raw.trigger),
    trigger_findings: Array.isArray(raw.trigger_findings)
      ? raw.trigger_findings.map(recordValue)
      : [],
    blast_radius: normalizeBlastRadius(raw.blast_radius),

    contract_sha: stringValue(raw.contract_sha, ""),
    compile_key: stringValue(raw.compile_key, ""),
    artifact_sha: stringValue(raw.artifact_sha, ""),

    validation_outcome: stringValue(raw.validation_outcome, "").toLowerCase(),
    validation: normalizeValidation(raw.validation),
    refusal: normalizeRefusal(raw.refusal),
    error: stringOrNull(raw.error),

    apply: normalizeApplySummary(raw.apply),
    decommission: normalizeDecommissionSummary(raw.decommission),
  };
}

/** `{surface: [in org, in the body we read]}`. Malformed pairs are dropped, not guessed. */
export function normalizeSourceCounts(input: unknown): Record<string, [number, number]> {
  const raw = asRecord(input);
  if (!raw) return {};
  const out: Record<string, [number, number]> = {};
  for (const [surface, value] of Object.entries(raw)) {
    if (!Array.isArray(value) || value.length < 2) continue;
    const org = numberOrNull(value[0]);
    const body = numberOrNull(value[1]);
    if (org == null || body == null) continue;
    out[surface] = [org, body];
  }
  return out;
}

/** `{kind, target, strength}` plus `{code, reason, manual_step}` on the dropped buckets. */
export function normalizeEffects(input: unknown): EngineEffect[] {
  if (!Array.isArray(input)) return [];
  return input.map((item) => {
    const raw = asRecord(item) ?? {};
    return {
      kind: stringValue(raw.kind, ""),
      target: stringValue(raw.target, ""),
      strength: arrayOfStrings(raw.strength),
      code: stringOrNull(raw.code) ?? undefined,
      reason: stringOrNull(raw.reason) ?? undefined,
      manual_step: stringOrNull(raw.manual_step) ?? undefined,
    };
  });
}

/**
 * `{code, source_ref, detail, manual_step}` — the engine's own spelling and the one
 * persisted in `residue_json` on every task row that exists. This used to read the
 * published contract's spelling as well; the wire settled it, so the alternative is
 * gone rather than kept "just in case", because a fallback that never fires is a line
 * of code claiming a risk it does not cover.
 */
export function normalizeResidue(input: unknown): EngineResidueItem[] {
  if (!Array.isArray(input)) return [];
  return input.map((item) => {
    const raw = asRecord(item) ?? {};
    return {
      code: stringValue(raw.code, ""),
      source_ref: stringValue(raw.source_ref, ""),
      detail: stringValue(raw.detail, ""),
      manual_step: stringValue(raw.manual_step, ""),
    };
  });
}

export function normalizeAdvisories(input: unknown): EngineAdvisory[] {
  if (!Array.isArray(input)) return [];
  return input
    .map((item) => {
      const raw = asRecord(item) ?? {};
      return {
        code: stringValue(raw.code, ""),
        severity: stringValue(raw.severity, ""),
        scope: arrayOfStrings(raw.scope),
        detail: stringValue(raw.detail, ""),
        manual_step: stringValue(raw.manual_step, ""),
      };
    })
    // An advisory with no code is not renderable and not actionable; keeping it would
    // add a row to a section whose whole purpose is that every row means something.
    .filter((advisory) => Boolean(advisory.code));
}

export function normalizeBlastRadius(input: unknown): EngineBlastRadius | null {
  const raw = asRecord(input);
  if (!raw || Object.keys(raw).length === 0) return null;
  const entries: EngineBlastEntry[] = (Array.isArray(raw.entries) ? raw.entries : []).map(
    (item) => {
      const entry = asRecord(item) ?? {};
      return {
        kind: stringValue(entry.kind, ""),
        name: stringValue(entry.name, ""),
        source: stringValue(entry.source, ""),
        detail: stringValue(entry.detail, ""),
        // Tri-state: an automation whose active flag we could not read is not inactive.
        active: typeof entry.active === "boolean" ? entry.active : null,
        stale_risk: booleanValue(entry.stale_risk),
      };
    }
  );
  const counts: Record<string, number> = {};
  for (const [kind, value] of Object.entries(recordValue(raw.counts))) {
    const count = numberOrNull(value);
    if (count != null) counts[kind] = count;
  }
  return {
    advisory: true,
    object: stringValue(raw.object, ""),
    fields: arrayOfStrings(raw.fields),
    entries,
    resolved: arrayOfStrings(raw.resolved),
    unavailable: arrayOfStrings(raw.unavailable),
    errors: (Array.isArray(raw.errors) ? raw.errors : []).map((item) => {
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(recordValue(item))) out[k] = stringValue(v, "");
      return out;
    }),
    counts,
  };
}

export function normalizeValidation(input: unknown): EngineValidation | null {
  const raw = asRecord(input);
  if (!raw) return null;
  const outcome = stringOrNull(raw.outcome);
  if (!outcome) return null;
  return {
    ...raw,
    // The wire lowercases at `_wire_task`; folded again so an uppercase audit constant
    // leaking past that boundary cannot become an unrecognised (badge-less) verdict.
    outcome: outcome.toLowerCase(),
    message: stringOrNull(raw.message) ?? undefined,
    errors: arrayOfStrings(raw.errors),
    reason: stringOrNull(raw.reason),
  };
}

export function normalizeRefusal(input: unknown): EngineRefusal | null {
  const raw = asRecord(input);
  if (!raw) return null;
  // `_refusal` defaults `code` to `outcome`, so either alone is a real refusal.
  const code = stringOrNull(raw.code) ?? stringOrNull(raw.outcome);
  if (!code) return null;
  return {
    code,
    outcome: stringOrNull(raw.outcome) ?? undefined,
    message: stringValue(raw.message, ""),
    objects: arrayOfStrings(raw.objects),
  };
}

/**
 * The write path, per task. EVERY FIELD FAILS CLOSED. `enabled`, `approved`,
 * `approval_matches_artifact` and `wrote_to_org` all default to `false` on an absent or
 * malformed payload, and `confirmation_token` defaults to the empty string — which no
 * typed token can equal, so a broken response can only ever make the apply path harder
 * to reach, never easier.
 */
export function normalizeApplySummary(input: unknown): EngineApplySummary {
  const raw = asRecord(input) ?? {};
  const willCreate: EngineWillCreate[] = (
    Array.isArray(raw.will_create) ? raw.will_create : []
  ).map((item) => {
    const entry = asRecord(item) ?? {};
    return {
      metadata_type: stringValue(entry.metadata_type, ""),
      component_key: stringValue(entry.component_key, ""),
    };
  });
  const attempts: EngineApplyAttempt[] = (Array.isArray(raw.attempts) ? raw.attempts : []).map(
    (item) => {
      const attempt = asRecord(item) ?? {};
      return {
        kind: stringValue(attempt.kind, ""),
        state: stringValue(attempt.state, "open"),
        deploy_id: stringOrNull(attempt.deploy_id),
        component_key: stringValue(attempt.component_key, ""),
        metadata_type: stringValue(attempt.metadata_type, ""),
        requested_at: stringOrNull(attempt.requested_at),
        submitted_at: stringOrNull(attempt.submitted_at),
        rolled_back_by: stringOrNull(attempt.rolled_back_by),
        preflight: recordValue(attempt.preflight),
        verification: normalizeVerification(attempt.verification),
      };
    }
  );
  return {
    will_create: willCreate,
    enabled: booleanValue(raw.enabled),
    approved: booleanValue(raw.approved),
    approval_id: stringValue(raw.approval_id, ""),
    approved_compile_key: stringValue(raw.approved_compile_key, ""),
    approval_matches_artifact: booleanValue(raw.approval_matches_artifact),
    confirmation_token: stringValue(raw.confirmation_token, ""),
    attempts,
    wrote_to_org: booleanValue(raw.wrote_to_org),
  };
}

/**
 * The post-deploy read-back. EVERY FIELD FAILS TOWARDS "WE DID NOT CHECK THAT".
 *
 * `read` defaults to `false` and `unread_surfaces` defaults to empty, and those two
 * defaults point in the same direction rather than opposite ones: a verification that
 * never arrived is not a verification, and `present: false` is what a renderer keys off so
 * an absent block reads as "no read-back is recorded" rather than as a clean comparison
 * that happened to carry no caveats.
 *
 * `unread_surfaces` is read under the server's own spelling and no other. It is the field
 * that separates "everything matched" from "everything we could read matched", and a
 * client that also accepted, say, `unreadable_surfaces` would have a branch nothing could
 * prove was live.
 */
export function normalizeVerification(input: unknown): EngineApplyVerification {
  const raw = asRecord(input);
  const present = Boolean(raw && Object.keys(raw).length > 0);
  return {
    outcome: stringValue(raw?.outcome, "").toLowerCase(),
    // `true` only on a JSON true. An unreadable payload did not read anything back.
    read: raw?.read === true,
    read_error: stringOrNull(raw?.read_error),
    unread_surfaces: arrayOfStrings(raw?.unread_surfaces),
    message: stringValue(raw?.message, ""),
    org_status: stringOrNull(raw?.org_status),
    flow_is_active: raw?.flow_is_active === true,
    diff: recordValue(raw?.diff),
    present,
  };
}

/**
 * The decommission block, per task. FAILS CLOSED EXACTLY AS `normalizeApplySummary` DOES,
 * and one field more so.
 *
 * `applicable`, `enabled`, `approved` and `approval_matches_artifact` all default to
 * `false`; `confirmation_token` defaults to `""`, which no typed token can equal. So a
 * response this normalizer cannot read can only ever make the deactivation harder to
 * reach, never easier — which is the only direction it is acceptable to be wrong in for a
 * request that stops a customer's business process.
 *
 * `double_fire_risk` is the exception AND IT IS DELIBERATE: it also defaults to `false`,
 * because it is a claim about the customer's org rather than a permission, and asserting
 * "both your automations may be firing" off a payload we could not parse would be raising
 * an alarm on no evidence. `phase` defaults to `""`, and a renderer reads that as "the
 * engine did not say" rather than as any of the five real phases.
 */
export function normalizeDecommissionSummary(input: unknown): EngineDecommissionSummary {
  const raw = asRecord(input) ?? {};
  const willDeactivate: EngineWillDeactivate[] = (
    Array.isArray(raw.will_deactivate) ? raw.will_deactivate : []
  ).map((item) => {
    const entry = asRecord(item) ?? {};
    return {
      metadata_type: stringValue(entry.metadata_type, ""),
      full_name: stringValue(entry.full_name, ""),
    };
  });
  return {
    applicable: booleanValue(raw.applicable),
    will_deactivate: willDeactivate,
    enabled: booleanValue(raw.enabled),
    approved: booleanValue(raw.approved),
    approval_id: stringValue(raw.approval_id, ""),
    approved_compile_key: stringValue(raw.approved_compile_key, ""),
    approval_matches_artifact: booleanValue(raw.approval_matches_artifact),
    confirmation_token: stringValue(raw.confirmation_token, ""),
    phase: stringValue(raw.phase, "") as EngineMigrationPhase,
    double_fire_risk: booleanValue(raw.double_fire_risk),
    // Tri-state: an engine that will not assert whether the source is running says `null`,
    // and that is a different sentence from "it is off".
    source_live: typeof raw.source_live === "boolean" ? raw.source_live : null,
    message: stringValue(raw.message, ""),
  };
}

/**
 * The result of a decommission or recommission. `sent` is a FIELD and never defaulted to
 * `true` — it fails closed in the only direction that is safe to be wrong in, which for
 * this act means "nothing reached the org", the state that makes a reader go and look.
 */
export function normalizeDecommissionResult(
  payload: unknown,
  planId: string
): EngineDecommissionResult {
  const raw = asRecord(payload) ?? {};
  const componentKey = stringValue(raw.component_key, "");
  return {
    plan_id: stringValue(raw.plan_id, planId),
    task_key: stringValue(raw.task_key, ""),
    outcome: stringValue(raw.outcome, "").toLowerCase(),
    sent: booleanValue(raw.sent),
    // Both name the SOURCE. `source` is the field the phase added; `component_key` is what
    // every existing reader of an apply result uses, and the server sends the same string
    // in both, so either alone identifies the customer's own component.
    source: stringValue(raw.source, componentKey),
    component_key: componentKey,
    metadata_type: stringValue(raw.metadata_type, ""),
    gate: stringValue(raw.gate, ""),
    code: stringOrNull(raw.code) ?? undefined,
    message: stringOrNull(raw.message) ?? undefined,
    expected: stringOrNull(raw.expected) ?? undefined,
    compile_key: stringOrNull(raw.compile_key) ?? undefined,
    deploy_id: stringOrNull(raw.deploy_id) ?? undefined,
  };
}

export function normalizeEffectsPage(
  payload: unknown,
  fallbackBucket: EngineCoverageBucket
): EngineEffectsPage {
  const raw = asRecord(payload) ?? {};
  const items = normalizeEffects(raw.items);
  return {
    plan_id: stringValue(raw.plan_id, ""),
    task_key: stringValue(raw.task_key, ""),
    bucket: (stringOrNull(raw.bucket) as EngineCoverageBucket | null) ?? fallbackBucket,
    total: numberValue(raw.total, items.length),
    limit: numberValue(raw.limit, items.length),
    offset: numberValue(raw.offset, 0),
    items,
  };
}

export function normalizeArtifactList(payload: unknown): EngineArtifactListResponse {
  const raw = asRecord(payload) ?? {};
  const source = Array.isArray(raw.artifacts) ? raw.artifacts : [];
  return {
    plan_id: stringValue(raw.plan_id, ""),
    task_key: stringValue(raw.task_key, ""),
    artifacts: source.map(normalizeArtifactRecord),
  };
}

export function normalizeArtifactRecord(input: unknown): EngineArtifactRecord {
  const raw = asRecord(input) ?? {};
  const componentKey = stringValue(raw.component_key, "");
  return {
    component_key: componentKey,
    component_key_encoded: stringValue(raw.component_key_encoded, encodeURIComponent(componentKey)),
    // The server sends the filename and the MIME type. A client-side extension map is a
    // second copy of a fact this response already carries, and it drifts the first time
    // the engine emits a family whose artifact is not `.xml`.
    filename: stringValue(raw.filename, componentKey),
    content_type: stringValue(raw.content_type, "text/plain"),
    size_bytes: numberValue(raw.size_bytes, 0),
    metadata_type: stringValue(raw.metadata_type, ""),
    version: numberValue(raw.version, 0),
    content_sha: stringValue(raw.content_sha, ""),
    compile_key: stringValue(raw.compile_key, ""),
    produced_by: stringValue(raw.produced_by, ""),
    superseded: booleanValue(raw.superseded),
    created_at: stringValue(raw.created_at, ""),
  };
}
